/**
 * "Connect to Data Room" — the single place a user connects a system.
 *
 * Renders the whole connector catalog and overlays live `source_registry`
 * state on it, so a system that already has data shows a tick (with its status
 * and freshness on hover — never an item count, epic locked decision 5)
 * instead of inviting a pointless re-connect. A connected system whose sync has
 * stopped keeping up loses the tick and says so. Registry lookup failing is not
 * fatal: the catalog still renders, just without ticks — the user can always
 * connect.
 *
 * Two different overlays, from two different reads, on purpose. HAS DATA comes
 * from `source_registry` and earns a tick. IS CONNECTED comes from
 * `GET /integrations/connections` — the live `cloud_accounts` rows the backend
 * freezes connect on — and removes the button entirely, because connecting is a
 * once-per-firm onboarding event and a second attempt returns 409. A demo
 * tenant has the first without the second.
 */
import React, { useCallback, useEffect, useState } from "react";
import connectIcon from "../assets/connect.svg";
import closeIcon from "../assets/cross.svg";
import { DATA_SOURCES, DataSource } from "../config/dataSources";
import { useDataSourceStatus } from "../hooks/useDataSourceStatus";
import { describeSource, hasData, hasSyncIssue } from "../utils/sourceStatus";
import DataSourceItem from "./DataSourceItem";

interface DataRoomConnectionPopupProps {
	isOpen: boolean;
	onClose: () => void;
	onConnect: (dataSource: DataSource) => void;
}

const DataRoomConnectionPopup: React.FC<DataRoomConnectionPopupProps> = ({
	isOpen,
	onClose,
	onConnect,
}) => {
	const [selectedDataSource, setSelectedDataSource] =
		useState<DataSource | null>(null);
	const { registered, bySystem, now, isLocked, needsReauthorization } =
		useDataSourceStatus(isOpen);

	const handleClose = useCallback(() => {
		setSelectedDataSource(null);
		onClose();
	}, [onClose]);

	// Escape closes, matching every other dismissible surface.
	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") handleClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [isOpen, handleClose]);

	const handleConnect = () => {
		// Re-checked at click time, not just at render: the connections read
		// resolves after the dialog paints, so a fast user can select a provider
		// while it still looks connectable and click once the lock has landed.
		if (selectedDataSource && !isLocked(selectedDataSource)) {
			onConnect(selectedDataSource);
			handleClose();
		}
	};

	if (!isOpen) return null;

	const connectedCount = registered.filter((r) => hasData(r.status)).length;
	const lockedCount = DATA_SOURCES.filter(isLocked).length;
	const selectedRow = selectedDataSource
		? bySystem[selectedDataSource.sourceSystem.toLowerCase()]
		: undefined;
	// A system that already has data is a re-sync, not a first connection.
	const connectVerb =
		(selectedRow && hasData(selectedRow.status)) ||
		(selectedDataSource && needsReauthorization(selectedDataSource))
			? "Reconnect"
			: "Connect";

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
			onClick={handleClose}
			role="presentation"
		>
			<div
				className="relative flex max-h-[90vh] w-full max-w-[567px] flex-col rounded-[13px] border border-gray-200 bg-white shadow-lg"
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby="connect-data-room-title"
			>
				<button
					onClick={handleClose}
					className="absolute right-5 top-4 flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
					aria-label="Close"
				>
					<img src={closeIcon} alt="" aria-hidden className="h-3 w-3" />
				</button>

				<div className="flex min-h-0 flex-col p-6">
					<div className="mb-4 pr-8">
						<h2
							id="connect-data-room-title"
							className="text-lg font-medium text-text-strong"
						>
							Connect Your Data Room
						</h2>
						<p className="mt-1.5 text-sm leading-relaxed text-text-muted">
							Seamlessly link your preferred storage provider to start syncing
							and analyzing your investment documents.
						</p>
						{connectedCount > 0 && (
							<p className="mt-2 text-xs font-medium text-text-muted">
								{connectedCount} of {DATA_SOURCES.length} already syncing —
								hover a tick for detail.
							</p>
						)}
						{lockedCount > 0 && (
							<p className="mt-1.5 text-xs font-medium text-text-muted">
								A connected provider is set up once for the whole firm. To point
								it at a different account, contact Doppler support.
							</p>
						)}
					</div>

					{/* Scrolls independently so the catalog can grow without the
					    dialog outgrowing the viewport. */}
					<div className="-mr-2 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-2">
						{DATA_SOURCES.map((dataSource) => {
							const row = bySystem[dataSource.sourceSystem.toLowerCase()];
							const connected = row && hasData(row.status);
							return (
								<DataSourceItem
									key={dataSource.id}
									dataSource={dataSource}
									onSelect={setSelectedDataSource}
									isSelected={selectedDataSource?.id === dataSource.id}
									connectedDetail={
										connected ? describeSource(row, now) : undefined
									}
									syncIssue={row ? hasSyncIssue(row, now) : false}
									isConnectionLocked={isLocked(dataSource)}
									reauthorizationRequired={needsReauthorization(dataSource)}
								/>
							);
						})}
					</div>

					<button
						disabled={!selectedDataSource || isLocked(selectedDataSource)}
						onClick={handleConnect}
						className="mt-5 flex h-[37px] w-full flex-shrink-0 items-center justify-center gap-3 rounded-md bg-[#004C4B] transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<img src={connectIcon} alt="" aria-hidden className="h-3.5 w-3.5" />
						<span className="text-sm font-medium text-white">
							{selectedDataSource
								? `${connectVerb} ${selectedDataSource.name}`
								: "Connect"}
						</span>
					</button>
				</div>
			</div>
		</div>
	);
};

export default DataRoomConnectionPopup;
